<template>
  <v-container
    fill
    fluid
    grid-list-xl>
    <v-form
      ref="auto"
      v-model="isAutoValidationOk"
      lazy-validation>
      <v-layout
        wrap>
        <template>
          <v-flex
            xs12>
            <span>
              {{ $t('Automatic leak test') }}
            </span>
            <hr>
          </v-flex>
          <v-flex
            xs6
            md3
            lg3>
            <v-switch
              v-model="tank.autoLeakTestEnabled"
              :label="`${ $t('Enabled') }`"
              class="dover-input text-xs-center" />
          </v-flex>
          <v-flex
            xs6
            md9
            lg9>
            <v-btn-toggle
              v-model="tank.autoLeakTestRepetition"
              mandatory
              @change="onTestModeChange()">
              <v-btn
                v-for="(item) in testModes"
                :key="item.index"
                :value="item.value"
                text
                small
                color="dover-blue">
                {{ item.desc }}
              </v-btn>
            </v-btn-toggle>
          </v-flex>
          <v-flex
            xs6
            md3
            lg3>
            <v-switch
              v-if="testModeControl === 0"
              v-model.number="autoLeakTestWarningPeriod"
              :label="`${ $t('NO TEST Warning') }`"
              class="dover-input"/>
            <v-text-field
              v-else-if="testModeControl === 1"
              v-model.number="autoLeakTestWarningPeriod"
              :counter="1"
              :rules="rules.weekLimit"
              :label="`${ $t('Leak test warning period (in days)') }`"
              class="dover-input"/>
            <v-text-field
              v-else-if="testModeControl === 2"
              v-model.number="autoLeakTestWarningPeriod"
              :counter="2"
              :rules="rules.monthLimit"
              :label="`${ $t('Leak test warning period (in days)') }`"
              class="dover-input"/>
            <v-text-field
              v-else
              v-model.number="autoLeakTestWarningPeriod"
              :counter="3"
              :rules="rules.yearLimit"
              :label="`${ $t('Leak test warning period (in days)') }`"
              class="dover-input"/>
          </v-flex>
          <v-flex
            xs6
            md3
            lg3>
            <v-text-field
              v-model.number="tank.autoLeakTestDuration"
              :counter="3"
              :rules="rules.duration"
              :label="`${ $t('Duration (min)') }`"
              mask="###"
              class="dover-input"/>
          </v-flex>
          <v-flex
            xs6
            md3
            lg3>
            <v-text-field
              v-model.number="tank.autoLeakTestVolumePercent"
              :counter="2"
              :rules="rules.perc"
              :label="`${ $t('Minimum fill (%)') }`"
              mask="##"
              class="dover-input"/>
          </v-flex>
          <v-flex
            xs6
            md3
            lg3>
            <v-text-field
              v-if="!isEPAProbe"
              v-model.number="customTankLeakRate"
              :counter="6"
              :rules="rules.leak"
              :label="`${ $t('Leak threshold') + ' (' + options.volumeUnits[systemSettings.volumeUnit || 0].desc + '/' + $t('H').toLowerCase() + ')' }`"
              class="dover-input"/>
          </v-flex>
          <v-flex
            xs12
            md12
            lg12
            text-xs-right>
            <v-btn
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              @click="saveTankSettings">
              {{ $t('Save') }}
            </v-btn>
          </v-flex>
        </template>
      </v-layout>
    </v-form>
    <v-form
      ref="form"
      v-model="isValidationOk"
      lazy-validation>
      <v-layout
        row
        wrap>
        <template>
          <v-flex
            xs12>
            <span>
              {{ $t('Scheduled leak test') }}
            </span>
            <hr>
          </v-flex>
          <v-flex
            xs6
            md4
            lg4>
            <MaglinkComponentDateTimePicker
              :component-key="1"
              :rules-is-date="rules.isDate"
              :rules="rules.required.concat(rules.time)"
              :initial-date="scheduledDate"
              :date-format="systemSettings.dateFormat"
              :time-format="timeFormat"
              :date-label="getDateLabel('Date')"
              :min-date="dateFromToday(0)"
              :max-date="dateFromToday(365)"
              :clear-date="false"
              :loading="false"
              @getDate="onGetDate"/>
          </v-flex>
          <v-flex
            v-show="isConfigured"
            xs12
            md4
            lg4>
            <v-text-field
              v-if="!isEPAProbe"
              v-model.number="tank.customTankLeakRate"
              :disabled="true"
              :label="`${ 'Leak threshold' }`"
              class="dover-input"/>
            <v-radio-group
              v-else
              v-model.number="testLeakageType"
              :row="true">
              <v-radio
                v-for="(element, index) in leakThresholds"
                :key="index"
                :label="`${ element.desc }`"
                :value="element.code"/>
            </v-radio-group>
          </v-flex>
          <v-flex
            v-show="isConfigured"
            xs6
            md2
            lg2>
            <v-text-field
              v-model.number="scheduledLeakTest.duration"
              :counter="3"
              :rules="rules.required.concat(rules.duration)"
              :label="`${ $t('Duration (min)') }`"
              mask="###"
              class="dover-input"/>
          </v-flex>
          <v-flex
            v-show="isConfigured"
            xs6
            md2
            lg2>
            <v-text-field
              v-model.number="scheduledLeakTest.volumePercent"
              :counter="2"
              :rules="rules.required.concat(rules.perc)"
              :label="`${ $t('Minimum fill (%)') }`"
              mask="##"
              class="dover-input"/>
          </v-flex>
          <v-flex
            v-show="!isConfigured"
            xs12>
            <span>
              {{ $t('No leak test scheduled') }}
            </span>
          </v-flex>
        </template>
      </v-layout>
      <v-layout
        row
        wrap>
        <v-flex
          xs12
          md12
          text-xs-right>
          <v-btn
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="saveScheduledLeakTest">
            {{ $t('Save') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-form>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { i18n } from '@/i18n'

export default {
  props: {
    tank: {
      type: Object,
      required: true,
      default: () => {}
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    scheduledLeakTest: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    isValidationOk: true,
    isAutoValidationOk: true,
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      perc: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 99) ],
      duration: [ v => Vue.prototype.Maglink.RulesRange(v, 30, 900) ],
      time: [ v => Vue.prototype.Maglink.MinDate(v, new Date()) ],
      isDate: [ v => true ],
      weekLimit: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 7) ],
      monthLimit: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 30) ],
      yearLimit: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 365) ],
      leak: [ v => Vue.prototype.Maglink.RulesRange(v, 3.785, 99.9) ]
    },
    isConfigured: false,
    leakThresholds: [],
    timeFormat: 'hh:mm',
    testModeControl: 3,
    testModes: [
      { index: 0, value: 1440, desc: i18n.t('Daily'), limit: 0 },
      { index: 1, value: 10080, desc: i18n.t('Weekly'), limit: 7 },
      { index: 2, value: 43200, desc: i18n.t('Monthly'), limit: 30 },
      { index: 3, value: 525600, desc: i18n.t('Yearly'), limit: 365 }
    ],
    warningPeriod: {
      isDaily: false,
      isWeekly: false,
      isMonthly: false,
      isYearly: true
    }
  }),
  computed: {
    isEPAProbe () {
      return (this.Maglink.isEPAProbe(this.tank.probeType))
    },
    // Convert the Test Warning period from Days to Minutes and vicevers
    autoLeakTestWarningPeriod: {
      get () {
        if (this.warningPeriod.isDaily) {
          if (this.tank.autoLeakTestWarningPeriod === 0) {
            return 0
          } else {
            return 1
          }
        } else {
          return this.tank.autoLeakTestWarningPeriod / 1440
        }
      },
      set (value) {
        if (this.warningPeriod.isDaily) {
          if (value === 0) {
            this.tank.autoLeakTestWarningPeriod = 0
          } else {
            this.tank.autoLeakTestWarningPeriod = 1440
          }
        } else {
          this.tank.autoLeakTestWarningPeriod = value * 1440
        }
      }
    },
    testLeakageType: {
      get () {
        return this.scheduledLeakTest.leakageType - 1
      },
      set (value) {
        this.scheduledLeakTest.leakageType = value + 1
      }
    },
    customTankLeakRate: {
      get () {
        return this.Maglink.LeakageToDisplay(this.tank.customTankLeakRate, this.systemSettings.volumeUnit || 0)
      },
      set (value) {
        this.tank.customTankLeakRate = this.Maglink.LeakageFromDisplay(value, this.systemSettings.volumeUnit || 0)
      }
    },

    scheduledDate () {
      if (this.scheduledLeakTest.time > 0) {
        return new Date(this.scheduledLeakTest.time * 1000)
      }
      return false
    }
  },
  watch: {
    tank: {
      immediate: true,
      deep: true,
      handler (newVal, oldVal) {
        var valueNew = true
        if (oldVal !== undefined) {
          valueNew = oldVal.autoLeakTestRepetition === newVal.autoLeakTestRepetition
        }

        if (valueNew) {
          if (newVal.autoLeakTestRepetition === this.testModes[0].value) {
            this.testModeControl = 0
          } else if (newVal.autoLeakTestRepetition === this.testModes[1].value) {
            this.testModeControl = 1
          } else if (newVal.autoLeakTestRepetition === this.testModes[2].value) {
            this.testModeControl = 2
          } else {
            this.testModeControl = 3
          }
        }
      }
    }
  },
  mounted () {
    this.leakThresholds = this.options.leakThresholds
    // remove 3.0 check
    this.leakThresholds.length = 2
    if (this.scheduledLeakTest.time > 0) {
      this.isConfigured = true
    }
    if (this.systemSettings.volumeUnit === 1) {
      this.rules.leak = [ v => Vue.prototype.Maglink.RulesRange(v, 1.0, 99.9) ]
    }
  },
  methods: {
    dateFromToday (days) {
      var currentDate = new Date()
      currentDate.setDate(currentDate.getDate() + days)
      return currentDate
    },
    onGetDate (componentKey, date) {
      if (!date) {
        this.isConfigured = false
        this.scheduledLeakTest.time = 0
      } else {
        this.isConfigured = true
        this.scheduledLeakTest.time = date.getTime() / 1000
      }
      this.checkDate()
    },
    getDateLabel (value) {
      return this.$t(value)
    },
    checkDate () {
      if (this.scheduledLeakTest.time === 0) {
        this.rules.isDate = [ v => true ]
        return true
      }

      var dt = new Date((this.scheduledLeakTest.time + 180) * 1000) // 3 minutes back
      if (dt < new Date()) {
        this.rules.isDate = [this.$t('Wrong date value')]
        return false
      }
      this.rules.isDate = [ v => true ]
      return true
    },
    saveScheduledLeakTest (event) {
      if (!this.checkDate()) {
        return
      }
      if (this.$refs.form.validate()) {
        this.isValidationOk = true
        if (this.isConfigured) {
          this.$emit('scheduledLeakTestSaved', this.tank.tankNumber)
        } else {
          this.$emit('scheduledLeakTestDeleted', this.tank.tankNumber)
        }
      } else {
        this.isValidationOk = false
      }
    },
    saveTankSettings (event) {
      if (this.$refs.auto.validate()) {
        this.isAutoValidationOk = true
        this.$emit('tankSaved', this.tank.tankNumber)
      } else {
        this.isAutoValidationOk = false
      }
    },

    onTestModeChange () {
      this.autoLeakTestWarningPeriod = 0
      this.tank.autoLeakTestDuration = 120
      this.tank.autoLeakTestVolumePercent = 50
      this.tank.customTankLeakRate = 1.0

      switch (this.tank.autoLeakTestRepetition) {
        case this.testModes[0].value:
          this.warningPeriod.isDaily = true
          this.warningPeriod.isWeekly = false
          this.warningPeriod.isMonthly = false
          this.warningPeriod.isYearly = false
          this.testModeControl = 0
          break

        case this.testModes[1].value:
          this.warningPeriod.isDaily = false
          this.warningPeriod.isWeekly = true
          this.warningPeriod.isMonthly = false
          this.warningPeriod.isYearly = false
          this.testModeControl = 1
          break

        case this.testModes[2].value:
          this.warningPeriod.isDaily = false
          this.warningPeriod.isWeekly = false
          this.warningPeriod.isMonthly = true
          this.warningPeriod.isYearly = false
          this.testModeControl = 2
          break

        default:
          this.warningPeriod.isDaily = false
          this.warningPeriod.isWeekly = false
          this.warningPeriod.isMonthly = false
          this.warningPeriod.isYearly = true
          this.testModeControl = 3
          break
      }
    }
  }
}
</script>
